import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Category, CategoryFolder } from "~/api/model";
import {
  getListOrganizationCategoriesQueryKey,
  getListOrganizationCategoryFoldersQueryKey,
  useListOrganizationCategoryFolders,
  useUpdateOrganizationCategory,
} from "~/api/organization/organization";
import { Button } from "~/components/ui/button";
import { Label } from "~/components/ui/label";
import { Modal } from "~/components/ui/modal";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { useOrganizationParams } from "~/helpers";
import { useToast } from "~/hooks/use-toast";

/**
 * MoveCategoryToFolderModal allows users to move a category to a different folder or to no folder.
 *
 * Features:
 * - Move category to a folder or to "No folder" (standalone category)
 * - Shows current location hint above dropdown
 * - Grouped dropdown: "No folder" option at top, separator, then folders group
 *
 * Server State Management:
 * - Uses React Query's useUpdateOrganizationCategory mutation to update category's folderId
 * - Invalidates both getListOrganizationCategoriesQueryKey and getListOrganizationCategoryFoldersQueryKey
 *   after successful update to refresh both categories and folders lists
 *
 * UX Notes:
 * - "No folder" is represented as null (not a folder object) - categories with folderId=null are standalone
 * - Current location is computed from category.folderId and folders list
 * - Dropdown value "null" (string) represents no folder; folder IDs are stored as strings in select state
 * - Modal closes automatically on successful move
 */
interface MoveCategoryToFolderModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  category: Category;
}

export default function MoveCategoryToFolderModal({
  open,
  onOpenChange,
  category,
}: MoveCategoryToFolderModalProps) {
  const { organizationId } = useOrganizationParams();
  const { data: folders } = useListOrganizationCategoryFolders(organizationId);
  const { mutateAsync: updateCategory } = useUpdateOrganizationCategory();
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [selectedFolderId, setSelectedFolderId] = useState<string>(
    category.folderId?.toString() ?? "null"
  );

  const currentLocation = useMemo(() => {
    if (!category.folderId) {
      return intl.formatMessage({ id: "category.moveToFolder.noFolder" });
    }
    const folder = folders?.find((f) => f.id === category.folderId);
    return folder?.name ?? "";
  }, [category.folderId, folders, intl]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const newFolderId =
      selectedFolderId === "null" ? null : Number(selectedFolderId);

    const categoriesQueryKey =
      getListOrganizationCategoriesQueryKey(organizationId);
    const foldersQueryKey =
      getListOrganizationCategoryFoldersQueryKey(organizationId);

    // Save previous data for rollback
    const previousCategoriesData =
      queryClient.getQueryData<Category[]>(categoriesQueryKey);
    const previousFoldersData =
      queryClient.getQueryData<CategoryFolder[]>(foldersQueryKey);

    // Optimistic update: immediately update category's folderId in categories cache
    queryClient.setQueryData(
      categoriesQueryKey,
      (oldData: Category[] | undefined) => {
        if (!oldData) return oldData;
        return oldData.map((c) =>
          c.id === category.id ? { ...c, folderId: newFolderId } : c
        );
      }
    );

    // Optimistic update: move category between folders in folders cache
    queryClient.setQueryData(
      foldersQueryKey,
      (oldData: CategoryFolder[] | undefined) => {
        if (!oldData) return oldData;

        const updatedCategory = { ...category, folderId: newFolderId };

        return oldData.map((folder) => {
          // Remove category from old folder
          if (folder.id === category.folderId) {
            return {
              ...folder,
              categories: folder.categories.filter((c) => c.id !== category.id),
            };
          }
          // Add category to new folder
          if (folder.id === newFolderId) {
            return {
              ...folder,
              categories: [...folder.categories, updatedCategory],
            };
          }
          return folder;
        });
      }
    );

    // Close modal immediately
    onOpenChange(false);

    // Update in background with success invalidation and error rollback
    updateCategory({
      organizationId: organizationId,
      categoryId: category.id.toString(),
      data: {
        folderId: newFolderId,
      },
    })
      .then(() => {
        // Invalidate both queries to sync with server data
        queryClient.invalidateQueries({ queryKey: categoriesQueryKey });
        queryClient.invalidateQueries({ queryKey: foldersQueryKey });
      })
      .catch(() => {
        // Rollback both caches
        queryClient.setQueryData(categoriesQueryKey, previousCategoriesData);
        queryClient.setQueryData(foldersQueryKey, previousFoldersData);
        toast({
          variant: "destructive",
          title: intl.formatMessage({ id: "error" }),
          description: intl.formatMessage({ id: "category.moveToFolderError" }),
        });
      });
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={intl.formatMessage({ id: "category.moveToFolder.title" })}
      description={intl.formatMessage({
        id: "category.moveToFolder.description",
      })}
      content={
        <form onSubmit={handleSubmit} id="move-category-to-folder-form">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>
                {intl.formatMessage({
                  id: "category.moveToFolder.selectFolder",
                })}
              </Label>

              {/* Current location hint */}
              <div className="text-sm text-muted-foreground">
                {intl.formatMessage(
                  { id: "category.moveToFolder.current" },
                  { destination: currentLocation }
                )}
              </div>

              <Select
                value={selectedFolderId}
                onValueChange={setSelectedFolderId}
              >
                <SelectTrigger className="h-10">
                  <SelectValue
                    placeholder={intl.formatMessage({
                      id: "category.moveToFolder.selectFolderPlaceholder",
                    })}
                  />
                </SelectTrigger>
                <SelectContent>
                  {/* No folder option */}
                  <SelectItem value="null">
                    {intl.formatMessage({
                      id: "category.moveToFolder.noFolder",
                    })}
                  </SelectItem>

                  {/* Separator and folders group */}
                  {folders && folders.length > 0 && (
                    <>
                      <SelectSeparator />
                      <SelectGroup>
                        <SelectLabel>
                          <FormattedMessage id="settings.folders" />
                        </SelectLabel>
                        {folders.map((folder) => (
                          <SelectItem
                            key={folder.id}
                            value={folder.id.toString()}
                          >
                            {folder.name}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
        </form>
      }
      footer={
        <>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            <FormattedMessage id="cancel" />
          </Button>
          <Button type="submit" form="move-category-to-folder-form">
            <FormattedMessage id="category.moveToFolder.move" />
          </Button>
        </>
      }
    />
  );
}
